import { useEffect, useState } from 'react';
import superagent from 'superagent';
import { useIdToken } from './useIdToken.js';
import config from '../client-config.js';
import qs from 'query-string';
import { PLATFORM } from 'constants/socialMedia.js';
import { ONE_TO_MANY_PRODUCT } from 'constants/products.js';
import { openPopup, waitForRedirectCallback, PopupBlockedError, PopupClosedError } from '../helpers/popup.jsx';
import { HTTP_ERROR_CODE } from 'constants/http.js';

export class AuthTokenExpiredError extends Error {}
export class AuthWindowBlockedError extends Error {}
export class AuthCancelledError extends Error {}
export class YouTubeAccountNotVerifiedError extends Error {}
export class FacebookMissingPermissionsError extends Error {}
export class FacebookInsufficientRoleError extends Error {}

/**
 * Whether a connected page is active for the given product on this BamHub. A page is
 * disabled (not active) when its `products` array doesn't include the product. Legacy
 * entries have no `products` field and are treated as live (ONE_TO_MANY_PRODUCT).
 * @param {{ products?: string[] }} page
 * @param {string} product
 * @returns {boolean}
 */
export const isPageActiveForProduct = (page, product) =>
  (!page.products && product === ONE_TO_MANY_PRODUCT) || !!page.products?.includes(product);

/** A cache to be used internally in this service. The code is quite simple, thus we dont import a dependency for this. */
const cache = {
  _store: new Map(),
  _ttl: new Map(),
  has(key) {
    return this._store.has(key);
  },
  get(key) {
    return this._store.get(key);
  },
  set(key, value, { ttl = 60 } = {}) {
    this.unset(key);
    this._store.set(key, value);
    this._ttl.set(key, setTimeout(() => this.unset(key), ttl * 1000));
  },
  unset(key) {
    if (!this.has(key)) return true;
    clearTimeout(this._ttl.get(key));
    this._store.delete(key);
    this._ttl.delete(key);
  },
  clear() {
    this._ttl.forEach(clearTimeout);
    this._ttl.clear();
    this._store.clear();
  },
};

/**
 * @typedef CustomRtmpSettings
 * @type {{
 *   customDisplayName: string,
 *   displayName: string,
 *   platform: 'customRtmp'
 *   streamKey: string,
 *   streamUrl: string,
 * }}
 */

/**
 * @typedef FacebookPage
 * @type {{
 *   id: string
 *   displayName: string
 *   pageId: string
 *   pageName: string
 *   pageToken: string
 *   platform: 'facebook'
 * }}
 */

/**
 * @typedef AdAccount
 * @type {{
 *   id: string
 *   accountId: string
 *   name: string
 *   currency: string
 *   accountStatus: number
 *   isActive: boolean
 *   pixels: { id: string, name: string }[]
 * }}
 */

/**
 * @typedef AdsEligibility
 * @type {{
 *   eligible: boolean
 *   pageEligible: boolean|null
 *   adAccountEligible: boolean|null
 *   accountStatus: number|null
 *   currency: string|null
 *   pixels: { id: string, name: string }[]
 *   reasons: { code: string, message?: string }[]
 * }}
 */

/**
 * @typedef MetaAd A show's Live Video Ad, as stored at `events:src/{showId}/metaAds/{connectionId}`
 * @type {{
 *   connectionId: string
 *   goal: 'views'|'sales'
 *   status: string
 *   effectiveStatus: string|null
 *   lifetimeBudgetCents: number
 *   currency: string|null
 *   targeting: { countries: string[], regions: { key: string, name: string }[], ageMin: number, ageMax: number, genders: number[] }|null
 *   audience: { type: 'saved', savedAudienceId: string, name: string, description: string|null, reach: SavedAudience['reach'], summary: SavedAudience['summary'], updatedAt: string|null }|null
 * }}
 */

/**
 * @typedef SavedAudience An audience saved in Meta Ads Manager, as the boost form offers it
 * @type {{
 *   id: string
 *   name: string
 *   description: string|null
 *   reach: { lower: number, upper: number }|null
 *   summary: { label: string, values: string[] }[]
 *   updatedAt: string|null
 * }}
 */

/**
 * @typedef YoutubeChannel
 * @type {{
 *   connectionName: string
 * }}
 */

/**
 * @typedef RequestOptions
 * @property {object} [headers={}]
 * @property {object} [params={}]
 * @property {any} [body]
 */

/**
 * @callback Request
 * @param {string} path
 * @param {any} body
 * @param {RequestOptions} options
 * @returns {superagent.SuperAgentRequest}
 */

/**
 * Creates a socialMediaAgent
 * @param {string} orgId The organization id for the agent
 * @param {string} idToken The id token to use for the agent
 */
const makeSocialMediaAgent = (orgId, idToken) => ({
  instance: {
    /**
     * @template {'get'|'post'|'put'|'patch'|'delete'} T
     * @param {T} method
     * @param {string} path
     * @param {RequestOptions} options
     * @returns {ReturnType<superagent.SuperAgentStatic[T]> extends superagent.SuperAgentRequest ? superagent.SuperAgentRequest : never}
     */
    req(method, path, { headers = {}, body, params = {} } = {}) {
      const region = encodeURIComponent(config.region || 'us-central1');
      const projectId = encodeURIComponent(config.projectId);
      const baseUrl = config.socialMediaBaseUrl || `https://${region}-${projectId}.cloudfunctions.net/socialMedia`;

      return superagent[method](`${baseUrl}/${path}`)
        .set({ 'content-type': 'application/json', ...headers, Authorization: `Bearer ${idToken}` })
        .query(params)
        .send(body);
    },
    /** @type {(path: string, options: RequestOptions) => superagent.SuperAgentRequest} */
    get(path, options) {
      return this.req('get', path, options);
    },
    /** @type {Request} */
    post(path, body, options) {
      return this.req('post', path, { body, ...options });
    },
    /** @type {Request} */
    put(path, body, options) {
      return this.req('put', path, { body, ...options });
    },
    /** @type {Request} */
    patch(path, body, options) {
      return this.req('patch', path, { body, ...options });
    },
    /** @type {(path: string, options: RequestOptions) => superagent.SuperAgentRequest} */
    delete(path, options) {
      return this.req('delete', path, { ...options });
    },
  },

  /**
   * @param {string} platform
   * @param {string} connectionId
   * @returns {Promise<any>}
   */
  async checkToken(platform, connectionId) {
    if (platform === PLATFORM.INSTAGRAM) platform = PLATFORM.FACEBOOK; // reuse the (relatively naïve) facebook endpoint for instagram
    const response = await this.instance.get(`${orgId}/${platform}/checkToken`, { params: { connectionId } });
    return response.body;
  },

  /**
   * @param {string} platform
   * @param {string} connectionId
   * @returns {Promise<{ sessionId: string, authUri: string }>}
   */
  async startConnect({ platform, redirectUri, scopes, configurationId, product }) {
    const queryParams = new URLSearchParams();
    queryParams.set('product', product);
    const response = await this.instance.post(
      `${orgId}/oauth/start?${queryParams.toString()}`,
      { platform, redirectUri, scopes, configurationId },
    );
    return response.body;
  },

  /**
   * @param {string} platform
   * @param {string} connectionId
   * @param {string} code
   * @param {string} state
   * @param {string} product
   * @returns {Promise<any>}
   */
  async completeConnect(platform, sessionId, code, state, product) {
    const queryParams = new URLSearchParams();
    queryParams.set('product', product);
    const response = await this.instance.post(
      `${orgId}/oauth/complete?${queryParams.toString()}`,
      { platform, sessionId, code, state },
    );
    return response.body;
  },

  /**
   * Every connected page for the platform on this BamHub. The endpoint is not product-scoped
   * (the per-product split is done client-side), so no product is sent.
   * @returns {Promise<FacebookPage[]>}
   */
  async getConnectedPages(platform) {
    const response = await this.instance.get(`${orgId}/${platform}/connectedPages`);
    return response.body;
  },

  /**
   * Cross-BamHub usage for the given accounts (token-safe).
   * @returns {Promise<{ usage: Record<string, Array<{ orgId: string, orgName?: string, products: string[], ugcVersion?: string }>> }>}
   */
  async getCrossOrgUsage(platform, pageIds = []) {
    const queryParams = new URLSearchParams({
      pageIds: pageIds.join(','),
    });
    const response = await this.instance.get(`${orgId}/${platform}/usage?${queryParams.toString()}`);
    return response.body;
  },

  /**
   * Set what an account is used for on this BamHub. Keyed by pageId so it works even
   * when this org has no doc for the account yet.
   * @returns {Promise<void>}
   */
  async setPageProduct(platform, pageId, { product, enabled, ugcVersion } = {}) {
    const query = new URLSearchParams({ product });
    await this.instance.patch(`${orgId}/${platform}/page/by-page-id/${pageId}/products?${query.toString()}`, {
      enabled,
      ...(typeof ugcVersion !== 'undefined' ? { ugcVersion } : {}),
    });
  },

  /**
   * Register interest in automated UGC curation for one connected account.
   * Idempotent server-side — repeat calls return the original stamp.
   * @param {string} platform
   * @param {string} pageId
   * @returns {Promise<{ automatedCurationRequestedAt: string }>}
   */
  async requestAutomatedUgcCuration(platform, pageId) {
    const response = await this.instance.post(`${orgId}/ugc/automated-curation-interest`, { platform, pageId });
    return response.body;
  },

  /**
   * @returns {Promise<YoutubeChannel>}
   */
  async getYoutubeChannel() {
    const response = await this.instance.get(`${orgId}/youtube/channel`);
    return response.body;
  },

  /**
   * @param {string} connectionId
   * @param {string} [product] When omitted, the whole connection is removed (all products)
   *   and the backend deauthorizes the login if no other BamHub uses it. When set, only
   *   that product is dropped (if the account is used for more than one).
   * @returns {Promise<void>}
   */
  async deleteFacebookPageConnection(connectionId, product) {
    const query = product ? `?${new URLSearchParams({ product }).toString()}` : '';
    await this.instance.delete(`${orgId}/facebook/page/${connectionId}${query}`);
  },

  /**
   * @returns {Promise<void>}
   */
  async deleteYoutubeConnection() {
    await this.instance.delete(`${orgId}/youtube`);
  },

  /**
   * @param {string} connectionId
   * @param {FacebookPage} data
   * @returns {Promise<void>}
   */
  async updateFacebookConnectionPage(connectionId, data) {
    await this.instance.put(`${orgId}/facebook/page/${connectionId}`, data);
  },

  /**
   * @param {string} connectionId
   * @returns {Promise<void>}
   */
  async syncFacebookPageDetails(connectionId) {
    await this.instance.post(`${orgId}/facebook/page/${connectionId}/sync/pageDetails`);
  },

  /**
   * @returns {Promise<CustomRtmpSettings[]>}
   */
  async getRTMPConnections() {
    const response = await this.instance.get(`${orgId}/customRtmp/platforms`);
    return response.body;
  },

  /**
   * @param {CustomRtmpSettings} data
   * @returns {Promise<string>}
   */
  async createRTMPConnection(data) {
    const response = await this.instance.post(`${orgId}/customRtmp/platforms`, data);
    return response.body;
  },

  /**
   * @param {string} rtmpId
   * @param {CustomRtmpSettings} data
   * @returns {Promise<void>}
   */
  async updateRTMPConnection(rtmpId, data) {
    await this.instance.put(`${orgId}/customRtmp/platforms/${rtmpId}`, data);
  },

  /**
   * @param {string} rtmpId
   * @returns {Promise<void>}
   */
  async deleteRTMPConnection(rtmpId) {
    await this.instance.delete(`${orgId}/customRtmp/platforms/${rtmpId}`);
  },

  /**
   * @returns {Promise<{ enabled: boolean }>}
   */
  async getTikTokRtmpEnabled() {
    const response = await this.instance.get(`${orgId}/tiktokRtmp/enabled`);
    return response.body;
  },

  /**
   * @param {boolean} enabled
   * @returns {Promise<{ enabled: boolean }>}
   */
  async setTikTokRtmpEnabled(enabled) {
    const response = await this.instance.put(`${orgId}/tiktokRtmp/enabled`, { enabled });
    return response.body;
  },

  // ── Catalog ──

  async setupCatalog(connectionId, catalogName) {
    const response = await this.instance.post(
      `${orgId}/facebook/page/${connectionId}/catalog/setup`,
      { ...(catalogName && { catalogName }) },
    );
    return response.body;
  },

  async syncCatalog(connectionId, items) {
    const response = await this.instance.post(
      `${orgId}/facebook/page/${connectionId}/catalog/sync`,
      { items },
    );
    return response.body;
  },

  /**
   * Whether the Page and one of its ad accounts can run Live Video Ads.
   * @param {string} connectionId
   * @param {string} accountId The unprefixed ad account id (digits only)
   * @returns {Promise<AdsEligibility>}
   */
  async getAdsEligibility(connectionId, accountId) {
    const response = await this.instance.get(
      `${orgId}/facebook/page/${encodeURIComponent(connectionId)}/ads/eligibility`,
      { params: { accountId } },
    );
    return response.body;
  },

  /**
   * Re-fetch the connection's ad accounts with the calling user's Facebook token.
   * @param {string} connectionId
   * @returns {Promise<{ adAccounts: AdAccount[] }>}
   */
  async syncAdAccounts(connectionId) {
    const response = await this.instance.post(`${orgId}/facebook/page/${encodeURIComponent(connectionId)}/sync/adAccounts`);
    return response.body;
  },

  /**
   * @param {{ connectionId: string, q: string, type: 'country'|'region' }} params
   * @returns {Promise<{ key: string, name: string, type: string, countryCode?: string }[]>}
   */
  async searchAdTargeting({ connectionId, q, type }) {
    const response = await this.instance.get(`${orgId}/facebook/ads/targeting/search`, { params: { connectionId, q, type } });
    return response.body;
  },

  /**
   * The ad account's saved audiences, from the backend's short cache unless `refresh` is set.
   * @param {{ connectionId: string, accountId: string, refresh?: boolean }} params
   * @returns {Promise<{ savedAudiences: SavedAudience[], fetchedAt: string }>}
   */
  async getSavedAudiences({ connectionId, accountId, refresh }) {
    const response = await this.instance.get(`${orgId}/facebook/ads/savedAudiences`, {
      params: { connectionId, accountId, ...(refresh && { refresh: true }) },
    });
    return response.body;
  },

  /**
   * @param {string} showId
   * @param {object} body
   * @returns {Promise<MetaAd>}
   */
  async createShowAd(showId, body) {
    const response = await this.instance.post(`${orgId}/facebook/shows/${encodeURIComponent(showId)}/ads`, body);
    return response.body;
  },

  /**
   * The ad, after the backend has refreshed its `effectiveStatus` from Meta.
   * @param {string} showId
   * @param {string} connectionId
   * @returns {Promise<MetaAd>}
   */
  async getShowAd(showId, connectionId) {
    const response = await this.instance.get(showAdPath(orgId, showId, connectionId));
    return response.body;
  },

  /**
   * @param {string} showId
   * @param {string} connectionId
   * @param {object} body
   * @returns {Promise<MetaAd>}
   */
  async updateShowAd(showId, connectionId, body) {
    const response = await this.instance.patch(showAdPath(orgId, showId, connectionId), body);
    return response.body;
  },

  /**
   * @param {string} showId
   * @param {string} connectionId
   * @returns {Promise<MetaAd>}
   */
  async pauseShowAd(showId, connectionId) {
    const response = await this.instance.post(`${showAdPath(orgId, showId, connectionId)}/pause`);
    return response.body;
  },

  /**
   * @param {string} showId
   * @param {string} connectionId
   * @returns {Promise<MetaAd>}
   */
  async resumeShowAd(showId, connectionId) {
    const response = await this.instance.post(`${showAdPath(orgId, showId, connectionId)}/resume`);
    return response.body;
  },

  /**
   * @param {string} showId
   * @param {string} connectionId
   * @returns {Promise<MetaAd>}
   */
  async cancelShowAd(showId, connectionId) {
    const response = await this.instance.delete(showAdPath(orgId, showId, connectionId));
    return response.body;
  },
});

const showAdPath = (orgId, showId, connectionId) =>
  `${orgId}/facebook/shows/${encodeURIComponent(showId)}/ads/${encodeURIComponent(connectionId)}`;

/**
 * Creates a social media service instance for the organization id using the id token
 * @param {string} orgId
 * @param {string} idToken
 */
const genService = (orgId, idToken) => {
  if (!idToken) return null;

  const socialMediaAgent = makeSocialMediaAgent(orgId, idToken);

  return {
    /**
     * @param {string} platform
     * @param {{ id?: string }} [options]
     * @returns {Promise<boolean>}
     */
    async isSocialMediaTokenValid(platform, { id } = {}) {
      if (platform === PLATFORM.CUSTOM_RTMP || platform === PLATFORM.TIKTOK_RTMP) return true;
      const cacheKey = `${orgId}:${idToken}:${platform}:${id}`;
      if (cache.has(cacheKey)) return cache.get(cacheKey);

      const result = socialMediaAgent.checkToken(platform, id).then(() => true).catch(() => false);
      cache.set(cacheKey, result);
      return result;
    },

    /**
     * @param {string} platform
     * @param {{ scopes?: string[] }} [options]
     * @returns {Promise<void>}
     */
    async connect(platform, { scopes, configurationId, product } = {}) {
      // Start by opening the popup, so that the browser recognises it as a direct result of a user action
      const redirectWindow = openPopup({ target: 'SocialMediaAuth' });
      const redirectDomain = config[platform]?.redirectDomain || `${window.top.location.protocol}//${window.top.location.host}`;
      // Meta only accepts HTTPS redirect URIs, which the HTTP local dev server can't offer.
      // Pointing this at a small HTTPS proxy page on labs.bambuser.com lets a local connect
      // hop Meta -> labs -> localhost. Unset everywhere else, so the dev server's own
      // /redirect-callback is used.
      const redirectUri = process.env.FACEBOOK_REDIRECT_URI || `${redirectDomain}/redirect-callback`;
      const { sessionId, authUri } = await socialMediaAgent.startConnect({ platform, redirectUri, scopes, configurationId, product });
      // Redirect the popup to the Uri provided by the API.
      redirectWindow.location.href = authUri;

      try {
        const completionHref = await waitForRedirectCallback(redirectWindow, 'SocialMediaAuth');
        const { code, state, error } = qs.parseUrl(completionHref).query;
        if (error) throw new AuthCancelledError();

        const result = await socialMediaAgent.completeConnect(platform, sessionId, code, state, product);

        cache.clear();
        // Surfaces e.g. { revokedAccounts } when a Business-Login re-grant dropped accounts.
        return result;
      } catch (err) {
        const errorCode = err.response && err.response.body && err.response.body.errorCode;
        if (err instanceof PopupClosedError) throw new AuthCancelledError();
        if (err instanceof PopupBlockedError) throw new AuthWindowBlockedError();
        if (errorCode === HTTP_ERROR_CODE.ACCOUNT_NOT_VERIFIED) throw new YouTubeAccountNotVerifiedError();
        if (errorCode === HTTP_ERROR_CODE.MISSING_PERMISSIONS) throw new FacebookMissingPermissionsError();
        if (errorCode === HTTP_ERROR_CODE.INSUFFICIENT_ROLE) throw new FacebookInsufficientRoleError();
        throw err;
      }
    },

    /**
     * Every connected page for the platform on this BamHub, INCLUDING ones not used for any
     * product (disabled). Adds `connectionName`. The endpoint is not product-scoped, so this
     * returns the full list; callers split active vs. disabled with `isPageActiveForProduct`.
     * @param {string} platform
     * @returns {Promise<({ connectionName: string } & FacebookPage)[]>}
     */
    async getAllConnectedPages(platform) {
      try {
        const { connectedPages } = await socialMediaAgent.getConnectedPages(platform);
        return (connectedPages || []).map(page => ({
          ...page,
          connectionName: page.customDisplayName || page.pageName,
        }));
      } catch (err) {
        if (err.status && err.status === 401) {
          throw new AuthTokenExpiredError();
        }
        throw err;
      }
    },

    /**
     * @param {string} platform
     * @param {string} product The product to filter the connected pages by. Defaults to ONE_TO_MANY_PRODUCT
     * @returns {Promise<({ connectionName: string } & FacebookPage)[]>}
     */
    async getConnectedPages(platform, product = ONE_TO_MANY_PRODUCT) {
      const pages = await this.getAllConnectedPages(platform);
      return pages.filter((page) => isPageActiveForProduct(page, product));
    },

    /**
     * Cross-BamHub usage for the given accounts, keyed by pageId.
     * @param {string} platform
     * @param {string[]} pageIds
     * @returns {Promise<Record<string, Array<{ orgId: string, orgName?: string, products: string[], ugcVersion?: string }>>>}
     */
    async getCrossOrgUsage(platform, pageIds = []) {
      if (!pageIds.length) return {};
      const { usage } = await socialMediaAgent.getCrossOrgUsage(platform, pageIds);
      return usage || {};
    },

    /**
     * Set what an account is used for on this BamHub (live and/or on-demand UGC).
     * `products: []` deselects it. Products are per-org and not globally unique;
     * social selling (the egress) is the single-owner field, set via the config PUT.
     * `ugcVersion`: 'v2' enables UGC, `null` clears it, `undefined` leaves the stored
     * value untouched (used when disabling the account so re-enabling restores it).
     * @param {string} platform
     * @param {string} pageId
     * @param {{ products: string[], ugcVersion?: string|null }} selection
     * @returns {Promise<void>}
     */
    async setPageProduct(platform, pageId, { product, enabled, ugcVersion } = {}) {
      await socialMediaAgent.setPageProduct(platform, pageId, { product, enabled, ugcVersion });
    },

    /**
     * Ask for AI curation to run automatically for one connected account. The stamp
     * it returns also lands on the account in getConnectedPages, so the UI can show
     * the account as already requested after a reload.
     * @param {string} platform
     * @param {string} pageId
     * @returns {Promise<{ automatedCurationRequestedAt: string }>}
     */
    async requestAutomatedUgcCuration(platform, pageId) {
      return socialMediaAgent.requestAutomatedUgcCuration(platform, pageId);
    },

    /**
     * @returns {Promise<YoutubeChannel | null>}
     */
    async getYoutubeChannel() {
      try {
        const channel = await socialMediaAgent.getYoutubeChannel();
        const tokenIsValid = await this.isSocialMediaTokenValid(PLATFORM.YOUTUBE);
        return { ...channel, expired: !tokenIsValid };
      } catch (err) {
        if (err.status && err.status === 404) {
          return null;
        }
        throw err;
      }
    },

    /**
     * @param {string} connectionId
     * @param {string} [product] The product to remove the connection from. When omitted, the
     *   whole connection is removed across all products (and the login is deauthorized when
     *   unused by any other BamHub).
     * @returns {Promise<void>}
     */
    async deleteFacebookPageConnection(connectionId, product) {
      await socialMediaAgent.deleteFacebookPageConnection(connectionId, product);
    },

    /**
     * @param {string} connectionId
     * @returns {Promise<void>}
     */
    async deleteYoutubeConnection() {
      await socialMediaAgent.deleteYoutubeConnection();
    },

    /**
     * @param {string} connectionId
     * @param {FacebookPage} connectionId
     * @returns {Promise<void>}
     */
    async updateFacebookConnectionPage(connectionId, data) {
      await socialMediaAgent.updateFacebookConnectionPage(connectionId, data);
    },

    /**
     * @param {string} connectionId
     * @returns {Promise<void>}
     */
    async syncFacebookPageDetails(connectionId) {
      await socialMediaAgent.syncFacebookPageDetails(connectionId);
    },

    /**
     * @returns {Promise<CustomRtmpSettings[]>}
     */
    async getConnectedCustomRtmpPlatforms() {
      const { socialMediaPlatforms = [] } = await socialMediaAgent.getRTMPConnections();

      return (socialMediaPlatforms).map(platform => {
        const { customDisplayName, displayName } = platform;
        platform.connectionName = customDisplayName ? ` ${displayName} - ${customDisplayName}` : ` ${displayName}`;
        return platform;
      });
    },

    /**
     * @param {CustomRtmpSettings} data
     * @returns {Promise<string>}
     */
    async createCustomRtmpIntegration(data) {
      return socialMediaAgent.createRTMPConnection(data);
    },

    /**
     * @param {string} rtmpId
     * @param {CustomRtmpSettings} data
     * @returns {Promise<void>}
     */
    async updateCustomRtmpSettings(rtmpId, data) {
      await socialMediaAgent.updateRTMPConnection(rtmpId, data);
    },

    /**
     * @param {string} rtmpId
     * @returns {Promise<void>}
     */
    async removeCustomRtmpIntegration(rtmpId) {
      await socialMediaAgent.deleteRTMPConnection(rtmpId);
    },

    /**
     * @returns {Promise<boolean>}
     */
    async getTikTokRtmpEnabled() {
      try {
        const { enabled } = await socialMediaAgent.getTikTokRtmpEnabled();
        return !!enabled;
      } catch (err) {
        if (err.status === 404) return false;
        throw err;
      }
    },

    /**
     * @param {boolean} enabled
     * @returns {Promise<boolean>}
     */
    async setTikTokRtmpEnabled(enabled) {
      const response = await socialMediaAgent.setTikTokRtmpEnabled(enabled);
      return !!response?.enabled;
    },

    // ── Catalog ──

    async setupCatalog(connectionId, catalogName) {
      return socialMediaAgent.setupCatalog(connectionId, catalogName);
    },

    async syncCatalog(connectionId, items) {
      return socialMediaAgent.syncCatalog(connectionId, items);
    },

    // Live Video Ads. Errors are passed through untouched: callers read the
    // `{ error: { code } }` envelope from `err.response.body`.
    getAdsEligibility: (connectionId, accountId) => socialMediaAgent.getAdsEligibility(connectionId, accountId),
    syncAdAccounts: (connectionId) => socialMediaAgent.syncAdAccounts(connectionId),
    searchAdTargeting: (params) => socialMediaAgent.searchAdTargeting(params),
    getSavedAudiences: (params) => socialMediaAgent.getSavedAudiences(params),
    createShowAd: (showId, body) => socialMediaAgent.createShowAd(showId, body),
    getShowAd: (showId, connectionId) => socialMediaAgent.getShowAd(showId, connectionId),
    updateShowAd: (showId, connectionId, body) => socialMediaAgent.updateShowAd(showId, connectionId, body),
    pauseShowAd: (showId, connectionId) => socialMediaAgent.pauseShowAd(showId, connectionId),
    resumeShowAd: (showId, connectionId) => socialMediaAgent.resumeShowAd(showId, connectionId),
    cancelShowAd: (showId, connectionId) => socialMediaAgent.cancelShowAd(showId, connectionId),
  };
};

/** @typedef {ReturnType<(typeof genService)>} SocialMediaService */

/**
 * Hooks a social media service instance to the current context
 * @param {string} orgId The organization id for the social media service
 * @returns {SocialMediaService}
 */
const useSocialMediaService = (orgId) => {
  const idToken = useIdToken();

  const [service, setService] = useState(() => genService(orgId, idToken));
  useEffect(() => void setService(genService(orgId, idToken)), [idToken, orgId]);

  return service;
};

export default useSocialMediaService;
